import React from 'react';
import PropTypes from 'prop-types';
import { Form, Col, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { SURVEY_INPUT_PROCESSING_STATES, READABLE_SURVEY_INPUT_PROCESSING_STATES } from '../../Constants';
import './FilterPanel.scss';

// Group processing statuses by workflow stage
const PROCESSING_STATUS_GROUPS = {
    'Initial States': [
        SURVEY_INPUT_PROCESSING_STATES.submitted,
        SURVEY_INPUT_PROCESSING_STATES.pendingLeadReview
    ],
    'Review Process': [
        SURVEY_INPUT_PROCESSING_STATES.readyForReview,
        SURVEY_INPUT_PROCESSING_STATES.inReview,
        SURVEY_INPUT_PROCESSING_STATES.readyForAdminQC
    ],
    'Flagged States': [
        SURVEY_INPUT_PROCESSING_STATES.flaggedForAdminReview,
        SURVEY_INPUT_PROCESSING_STATES.flaggedForLeadReview,
        SURVEY_INPUT_PROCESSING_STATES.awaitingClientResponse
    ],
    'Final States': [
        SURVEY_INPUT_PROCESSING_STATES.reviewComplete,
        SURVEY_INPUT_PROCESSING_STATES.accepted
    ]
};

// Status descriptions for tooltips
const STATUS_DESCRIPTIONS = {
    [SURVEY_INPUT_PROCESSING_STATES.submitted]: 'Initial submission, pending review assignment',
    [SURVEY_INPUT_PROCESSING_STATES.pendingLeadReview]: 'Awaiting lead reviewer assessment',
    [SURVEY_INPUT_PROCESSING_STATES.readyForReview]: 'Ready for detailed review process',
    [SURVEY_INPUT_PROCESSING_STATES.inReview]: 'Currently under active review',
    [SURVEY_INPUT_PROCESSING_STATES.flaggedForAdminReview]: 'Requires administrator attention',
    [SURVEY_INPUT_PROCESSING_STATES.flaggedForLeadReview]: 'Requires lead reviewer attention',
    [SURVEY_INPUT_PROCESSING_STATES.awaitingClientResponse]: 'Waiting for client feedback',
    [SURVEY_INPUT_PROCESSING_STATES.readyForAdminQC]: 'Ready for final quality check',
    [SURVEY_INPUT_PROCESSING_STATES.reviewComplete]: 'Review process completed',
    [SURVEY_INPUT_PROCESSING_STATES.accepted]: 'Survey accepted and ready for use'
};

const FilterPanel = ({
    processingStatus,
    setProcessingStatus,
    sensitive,
    setSensitive
}) => {
    const renderStatusOption = (status) => (
        <OverlayTrigger
            key={status}
            placement="right"
            overlay={
                <Tooltip id={`tooltip-${status}`}>
                    {STATUS_DESCRIPTIONS[status]}
                </Tooltip>
            }
        >
            <option value={status}>
                {READABLE_SURVEY_INPUT_PROCESSING_STATES[status]}
            </option>
        </OverlayTrigger>
    );

    return (
        <section className="filter-panel" aria-label="Survey questionnaire filters">
            <div className="filter-panel-header">
                <h2>Filter Options</h2>
                <p className="text-muted">Refine the questionnaire submission list using the filters below</p>
            </div>
            
            <Row>
                <Col md={8}>
                    <Form.Group className="filter-group">
                        <Form.Label>Processing Status</Form.Label>
                        <Form.Select
                            value={processingStatus}
                            onChange={(e) => setProcessingStatus(e.target.value)}
                            aria-label="Select processing status"
                        >
                            <option value="">All Statuses</option>
                            {Object.entries(PROCESSING_STATUS_GROUPS).map(([groupName, statuses]) => (
                                <optgroup key={groupName} label={groupName}>
                                    {statuses.map(status => renderStatusOption(status))}
                                </optgroup>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Filter questionnaires by their current processing stage
                        </Form.Text>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="filter-group">
                        <Form.Label>
                            Sensitive Content
                            <OverlayTrigger
                                placement="right"
                                overlay={
                                    <Tooltip id="tooltip-sensitive">
                                        Filter questionnaires containing sensitive information
                                    </Tooltip>
                                }
                            >
                                <span className="ms-2 sensitive-info-icon">ⓘ</span>
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Select
                            value={sensitive === undefined ? "" : sensitive.toString()}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSensitive(value === "" ? undefined : value === "true");
                            }}
                            aria-label="Filter by sensitive content"
                        >
                            <option value="">All Content</option>
                            <option value="true">Sensitive Only</option>
                            <option value="false">Non-Sensitive Only</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Filter by content sensitivity level
                        </Form.Text>
                    </Form.Group>
                </Col>
            </Row>
        </section>
    );
};

FilterPanel.propTypes = {
    processingStatus: PropTypes.string.isRequired,
    setProcessingStatus: PropTypes.func.isRequired,
    sensitive: PropTypes.bool,
    setSensitive: PropTypes.func.isRequired
};

export default FilterPanel; 